import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from '../../../components/modal/GenericModal';
import Select from 'react-select';
import RubricaUpload, { RubricaContainer } from './RubricaUpload';
import { AuthContext } from '../../../hooks/context/authContext';
import { downloadFile } from '../../../consumers/backendConsumer';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { usePostRequest } from '../../../hooks/usePostRequest/usePostRequest';
import {
    crearClaseModeloAPI,
    actualizarClaseModeloAPI,
    listarRubricasClaseModeloAPI,
    guardarRubricaClaseModeloAPI,
    getTPSedesAPI,
    consultarSedeTrasladosPromocionesAPI,
    getColaboradoresPorSedeAPI,
} from '../../../consumers/backendApisUrls';

const ROL_GESTION_TRASLADOS_PROMOCIONES = 83;

const getData = (response) => response?.data?.Data ?? response?.data?.data ?? response?.data ?? {};

const normalizarOptions = (response) => {
    const data = getData(response);
    const rows = Array.isArray(data) ? data : (data?.Items ?? data?.items ?? []);
    return rows.map(item => ({
        value: item.Value ?? item.value ?? item.IdSede ?? item.idSede ?? item.Id ?? item.id,
        label: item.Text ?? item.text ?? item.Descripcion ?? item.descripcion ?? item.DesSede ?? item.desSede ?? item.Nombre ?? item.nombre,
        raw: item,
    })).filter(item => item.value != null);
};

const valor = (...candidatos) => candidatos.find(v => v !== undefined && v !== null && v !== '') ?? '';

const ProgramarClaseModelo = ({ toggleModal, refreshPage, row }) => {
    const { state } = useContext(AuthContext);
    const puedeProgramar = (state?.roleIds ?? [])
        .map(Number)
        .includes(ROL_GESTION_TRASLADOS_PROMOCIONES);

    const [executeGet] = useGetRequest();
    const [executePost] = usePostRequest();

    const [sedes, setSedes] = useState([]);
    const [sedeEvaluadoraId, setSedeEvaluadoraId] = useState('');
    const [sedeOrigenId, setSedeOrigenId] = useState('');
    const [liderEvaluador, setLiderEvaluador] = useState(null);
    const [liderOrigen, setLiderOrigen] = useState(null);
    const [colaboradoresEvaluador, setColaboradoresEvaluador] = useState([]);
    const [modalReasignar, setModalReasignar] = useState(false);
    const [nuevoResponsable, setNuevoResponsable] = useState(null);
    const [fechaLimite, setFechaLimite] = useState('');
    const [rubricas, setRubricas] = useState([]);
    const [subiendoRubrica, setSubiendoRubrica] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const postulante = row ?? {};
    const postulanteId = valor(postulante?.idPostulante, postulante?.IdPostulante, '');
    const idClaseModelo = valor(postulante?.idClaseModelo, postulante?.IdClaseModelo, '');

    // CA-03: datos del candidato, no del responsable de la convocatoria.
    const nombreCandidato = valor(
        postulante?.nombrePostulante,
        postulante?.NombrePostulante,
        postulante?.nombreCompleto,
        postulante?.NombreCompleto,
        '-'
    );
    const documento = valor(
        postulante?.dni,
        postulante?.Dni,
        postulante?.DNI,
        postulante?.numeroDocumento,
        postulante?.NumeroDocumento,
        postulante?.documento,
        postulante?.Documento,
        '-'
    );
    const puestoActual = valor(
        postulante?.desCargo,
        postulante?.DesCargo,
        postulante?.puestoActual,
        postulante?.PuestoActual,
        '-'
    );

    const obtenerLiderSede = async (idSede) => {
        if (!idSede) return null;

        try {
            const response = await executeGet(consultarSedeTrasladosPromocionesAPI(idSede));
            const sede = getData(response);
            const idEmpleado = valor(sede?.IdEmpleadoLider, sede?.idEmpleadoLider, '');
            const nombreLider = valor(sede?.EmpleadoLider, sede?.empleadoLider, '');

            return idEmpleado && nombreLider
                ? { value: Number(idEmpleado), label: nombreLider }
                : null;
        } catch (error) {
            console.error('Error obteniendo líder de sede', error);
            return null;
        }
    };

    const obtenerColaboradoresSede = async (idSede) => {
        if (!idSede) {
            setColaboradoresEvaluador([]);
            return;
        }

        try {
            const response = await executeGet(
                getColaboradoresPorSedeAPI(idSede)
            );

            setColaboradoresEvaluador(normalizarOptions(response));
        } catch (error) {
            console.error(
                'Error al obtener colaboradores de la sede evaluadora',
                error
            );
            setColaboradoresEvaluador([]);
        }
    };

    useEffect(() => {
        if (!puedeProgramar) {
            setIsLoading(false);
            return;
        }

        const cargar = async () => {
            setIsLoading(true);
            try {
                const sedesResponse = await executeGet(getTPSedesAPI());
                setSedes(normalizarOptions(sedesResponse));

                if (postulanteId) {
                    const rubricasResponse = await executeGet(listarRubricasClaseModeloAPI(postulanteId));
                    const rubricasData = getData(rubricasResponse);
                    const listaRubricas = Array.isArray(rubricasData)
                        ? rubricasData
                        : (rubricasData?.Items ?? rubricasData?.items ?? []);
                    setRubricas(listaRubricas.slice(0, 1));
                }

                const sedeEvaluadoraInicial = valor(postulante?.idSedeEvaluadora, postulante?.IdSedeEvaluadora, '');
                const sedeOrigenInicial = valor(postulante?.idSedeOrigen, postulante?.IdSedeOrigen, postulante?.idSede, postulante?.IdSede, '');

                setSedeEvaluadoraId(sedeEvaluadoraInicial);
                setSedeOrigenId(sedeOrigenInicial);
                setFechaLimite((valor(postulante?.fechaLimiteClaseModelo, postulante?.FechaLimiteClaseModelo, '') || '').slice(0, 10));

                if (sedeEvaluadoraInicial) {
                    setLiderEvaluador(await obtenerLiderSede(sedeEvaluadoraInicial));
                }
                if (sedeOrigenInicial) {
                    setLiderOrigen(await obtenerLiderSede(sedeOrigenInicial));
                }
            } catch (error) {
                console.error('Error cargando datos de clase modelo', error);
                toast.error('No se pudieron cargar los datos para programar la clase modelo.');
            } finally {
                setIsLoading(false);
            }
        };

        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSedeEvaluadoraChange = async (e) => {
        const idSede = e.target.value;
        setSedeEvaluadoraId(idSede);
        setLiderEvaluador(null);
        setColaboradoresEvaluador([]);
        setNuevoResponsable(null);
        if (idSede) {
            setLiderEvaluador(await obtenerLiderSede(idSede));
        }
    };

    const handleSedeOrigenChange = async (e) => {
        const idSede = e.target.value;
        setSedeOrigenId(idSede);
        setLiderOrigen(null);
        if (idSede) setLiderOrigen(await obtenerLiderSede(idSede));
    };

    // Reutiliza el mismo flujo de Convocatoria:
    // GenericUploadFileV2 sube primero a S3/temporary y devuelve un FileViewModel.
    // Luego GuardarRubricaClaseModelo lo asocia al postulante y mueve el archivo.
    const handleAgregarRubrica = async (s3Response) => {
        if (!s3Response?.Path || !postulanteId) return;

        try {
            const response = await executePost(
                guardarRubricaClaseModeloAPI(postulanteId),
                { File: s3Response }
            );

            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo guardar la rúbrica.');
                return;
            }

            const rubricaGuardada = getData(response);
            setRubricas([rubricaGuardada]);
            toast.success('Rúbrica cargada correctamente.');
        } catch (error) {
            console.error('Error guardando rúbrica de clase modelo', error);
            toast.error('No se pudo guardar la rúbrica.');
        }
    };

    const handleDescargarRubrica = (archivo) => {
        if (!archivo?.Path) return;
        downloadFile(state.token, archivo.Path, archivo.OriginalFileName);
    };

    const validar = () => {
        if (!postulanteId) return 'No se encontró el candidato seleccionado.';
        if (!sedeEvaluadoraId) return 'Selecciona la sede evaluadora.';
        if (!liderEvaluador?.value) return 'No se encontró el líder de la sede evaluadora.';
        if (!sedeOrigenId) return 'Selecciona la sede de origen.';
        if (!liderOrigen?.value) return 'No se encontró el líder de la sede de origen.';
        if (!fechaLimite) return 'Ingresa la fecha límite.';
        if (!rubricas.length) return 'Debes cargar la rúbrica de evaluación.';
        return null;
    };


    const guardar = async (idEstadoPostulacion) => {
        if (!puedeProgramar) {
            toast.warning('No tienes permisos para programar la clase modelo.');
            return;
        }

        const requiereProgramacion = idEstadoPostulacion === 71;
        if (requiereProgramacion) {
            const mensaje = validar();
            if (mensaje) {
                toast.warning(mensaje);
                return;
            }
        }
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const ahora = new Date().toISOString();
            const idUsuario = state?.user?.id_usuario ?? state?.user?.id_empleado ?? null;
            const payload = {
                idClaseModelo: idClaseModelo ? Number(idClaseModelo) : null,
                idSedeEvaluadora: sedeEvaluadoraId ? Number(sedeEvaluadoraId) : null,
                idEmpleadoEvaluador: liderEvaluador?.value ? Number(liderEvaluador.value) : null,
                idSedeOrigen: idEstadoPostulacion === 65 ? null : (sedeOrigenId ? Number(sedeOrigenId) : null),
                fechaLimite: fechaLimite ? `${fechaLimite}T23:59:59` : null,
                comentarioEvaluador: null,
                idPostulante: Number(postulanteId),
                usuarioCrea: idUsuario,
                usuarioModifica: idUsuario,
                fechaCrea: postulante?.fechaCreaClaseModelo ?? postulante?.FechaCreaClaseModelo ?? ahora,
                fechaModifica: ahora,
                motivoObservado: null,
                idEstadoPostulacion,
            };

            const endpoint = idClaseModelo
                ? actualizarClaseModeloAPI()
                : crearClaseModeloAPI();
            const response = await executePost(endpoint, payload);
            const ok = response?.status >= 200 && response?.status < 300;
            if (!ok) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo registrar la clase modelo.');
                return;
            }

            if (idEstadoPostulacion === 65) toast.success('Etapa de Clase Modelo omitida correctamente.');
            else if (idEstadoPostulacion === 48) toast.success('Desistimiento registrado correctamente.');
            else toast.success('Clase modelo configurada correctamente.');

            refreshPage && refreshPage();
            toggleModal();
        } catch (error) {
            console.error('Error registrando clase modelo', error);
            toast.error('No se pudo registrar la clase modelo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GenericModal showModal={toggleModal} titulo="Configuración GDH - Clase modelo" width="900px">
            <ModalBody>
                <CampaignBar>
                    {row?.nombreConvocatoria ?? 'Nombre campaña'} - Programación de clase modelo
                </CampaignBar>

                {!puedeProgramar ? (
                    <div className="text-center py-5">Esta vista corresponde a la programación de Clase Modelo por GDH.</div>
                ) : isLoading ? (
                    <div className="text-center py-5">Cargando información...</div>
                ) : (
                    <>
                        <Section>
                            <SectionTitle>Datos del candidato</SectionTitle>
                            <div className="row">
                                <ReadField className="col-md-5">
                                    <label>Nombre del candidato</label>
                                    <div>{nombreCandidato}</div>
                                </ReadField>
                                <ReadField className="col-md-3">
                                    <label>DNI / CEX</label>
                                    <div>{documento}</div>
                                </ReadField>
                                <ReadField className="col-md-4">
                                    <label>Puesto actual</label>
                                    <div>{puestoActual}</div>
                                </ReadField>
                            </div>
                        </Section>

                        <Section>
                            <SectionTitle>Datos de programación</SectionTitle>
                            <div className="row">
                                <div className="col-md-6 form-group">
                                    <label>Sede evaluadora</label>
                                    <select className="form-control" value={sedeEvaluadoraId} onChange={handleSedeEvaluadoraChange}>
                                        <option value="">Seleccionar sede</option>
                                        {sedes.map(sede => <option key={sede.value} value={sede.value}>{sede.label}</option>)}
                                    </select>
                                </div>

                                <div className="col-md-6 form-group">
                                    <label>Líder de sede evaluadora</label>
                                    <LeaderLine>
                                        <LeaderValue>{liderEvaluador?.label ?? '-'}</LeaderValue>
                                        <button
                                            type="button"
                                            className="generic-button-buscador"
                                            onClick={async () => {
                                                setNuevoResponsable(null);
                                                await obtenerColaboradoresSede(sedeEvaluadoraId);
                                                setModalReasignar(true);
                                            }}
                                        >
                                            Reasignar
                                        </button>
                                    </LeaderLine>
                                </div>

                                <div className="col-md-6 form-group">
                                    <label>Sede de origen</label>
                                    <select className="form-control" value={sedeOrigenId} onChange={handleSedeOrigenChange}>
                                        <option value="">Seleccionar sede</option>
                                        {sedes.map(sede => <option key={sede.value} value={sede.value}>{sede.label}</option>)}
                                    </select>
                                </div>

                                <ReadField className="col-md-6">
                                    <label>Líder de sede de origen</label>
                                    <div>{liderOrigen?.label ?? '-'}</div>
                                </ReadField>

                                <div className="col-md-6 form-group mt-3">
                                    <label>Fecha límite</label>
                                    <input type="date" className="form-control" value={fechaLimite} onChange={e => setFechaLimite(e.target.value)} />
                                </div>
                            </div>
                        </Section>

                        <Section>
                            <RubricaContainer>
                                <SectionTitle className="mb-0">Rúbrica de evaluación</SectionTitle>

                                {/* Solo la rúbrica vigente (1 archivo, se reemplaza al subir
                                    otra) — ver handleAgregarRubrica. */}
                                <RubricaUpload
                                    id="clase-modelo-rubrica"
                                    archivos={rubricas[0] ? [rubricas[0]] : []}
                                    onDescargar={handleDescargarRubrica}
                                    uploadFileToS3={handleAgregarRubrica}
                                    isIcon
                                    toolTipText={rubricas[0] ? 'Reemplazar rúbrica' : 'Subir archivo'}
                                    extensionesValidas={["pdf", "doc", "docx", "xls", "xlsx"]}
                                    resetInitialFile={false}
                                    subiendoArchivoPublicState={subiendoRubrica}
                                    setSubiendoArchivoPublicState={setSubiendoRubrica}
                                />
                            </RubricaContainer>
                        </Section>

                        <FooterActions>
                            <OmitirButton
                                type="button"
                                onClick={() => guardar(65)}
                                disabled={isSubmitting}
                            >
                                <span>Omitir etapa y<br />continuar</span>
                            </OmitirButton>

                            <FooterButton
                                type="button"
                                onClick={() => guardar(71)}
                                disabled={isSubmitting}
                            >
                                <span>{isSubmitting ? 'Enviando...' : <>Continuar y enviar<br />correo</>}</span>
                                <FontAwesomeIcon icon="save" className="continue-icon" />
                            </FooterButton>

                            <FooterButton
                                type="button"
                                onClick={() => guardar(48)}
                                disabled={isSubmitting}
                            >
                                <span>Desiste</span>
                                <FontAwesomeIcon icon="times-circle" className="desiste-icon" />
                            </FooterButton>
                        </FooterActions>
                    </>
                )}
            </ModalBody>

            {modalReasignar && (
                <ReasignarOverlay>
                    <ReasignarModal>
                        <ReasignarHeader>
                            <span>Reasignar responsable</span>
                            <button type="button" onClick={() => setModalReasignar(false)}>
                                <FontAwesomeIcon icon="times-circle" />
                            </button>
                        </ReasignarHeader>

                        <ReasignarBody>
                            <div className="form-group">
                                <label>Responsable actual</label>
                                <LeaderValue>{liderEvaluador?.label ?? '-'}</LeaderValue>
                            </div>

                            <div className="form-group">
                                <label>Nuevo responsable</label>
                                <Select
                                    options={colaboradoresEvaluador}
                                    onChange={setNuevoResponsable}
                                    value={nuevoResponsable}
                                    placeholder="Buscar nuevo responsable..."
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    maxMenuHeight={280}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 10000 })
                                    }}
                                />
                            </div>

                            <ReasignarFooter>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!nuevoResponsable) {
                                            toast.warning('Seleccione un nuevo responsable.');
                                            return;
                                        }
                                        setLiderEvaluador(nuevoResponsable);
                                        setModalReasignar(false);
                                    }}
                                >
                                    Guardar
                                </button>
                            </ReasignarFooter>
                        </ReasignarBody>
                    </ReasignarModal>
                </ReasignarOverlay>
            )}
        </GenericModal>
    );
};

export default ProgramarClaseModelo;

const ModalBody = styled.div`
    padding: 18px 24px 24px;
    label { font-weight: 600; margin-bottom: 5px; }
`;

const CampaignBar = styled.div`
    background: #0755ef;
    color: white;
    padding: 12px 16px;
    margin-bottom: 20px;
    font-weight: 600;
`;

const Section = styled.div`
    border-top: 3px solid #0755ef;
    padding: 18px 4px 14px;
    margin-top: 12px;
`;

const SectionTitle = styled.div`
    display: inline-block;
    min-width: 190px;
    background: #0755ef;
    color: white;
    padding: 7px 12px;
    margin-bottom: 16px;
    font-weight: 600;
`;

const ReadField = styled.div`
    margin-bottom: 16px;
    div { min-height: 38px; background: #f1f1f1; padding: 9px 12px; border-radius: 3px; }
`;

const LeaderLine = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const LeaderValue = styled.div`
    flex: 1;
    min-height: 38px;
    background: #f1f1f1;
    padding: 9px 12px;
    border-radius: 3px;
`;

const ReasignarOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const ReasignarModal = styled.div`
    width: 590px;
    max-width: calc(100vw - 40px);
    background: white;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
`;

const ReasignarHeader = styled.div`
    height: 48px;
    padding: 0 22px;
    background: #0871ad;
    color: white;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: space-between;

    button {
        border: 0;
        background: transparent;
        color: white;
        cursor: pointer;
    }
`;

const ReasignarBody = styled.div`
    padding: 28px 34px 30px;
`;

const ReasignarFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;

    button {
        min-width: 112px;
        height: 36px;
        border: 0;
        border-radius: 18px;
        background: #8cc327;
        color: white;
        cursor: pointer;
    }
`;

const FooterActions = styled.div`
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    gap: 24px;
    width: 100%;
    flex-wrap: nowrap;
`;

const FooterButton = styled.button`
    flex: 1;
    min-width: 0;
    height: 52px;
    padding: 0 22px;
    border: 0;
    border-radius: 28px;
    background: #3493d1;
    color: white;
    font-size: 18px;
    line-height: 20px;
    font-weight: 400;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    cursor: pointer;

    > span {
        flex: 1;
        text-align: center;
    }

    svg {
        flex-shrink: 0;
        font-size: 16px;
    }

    .continue-icon {
        color: #0755a0;
    }

    .desiste-icon {
        color: #e64b4b;
    }

    &:disabled {
        opacity: 0.65;
        cursor: default;
    }
`;

const OmitirButton = styled(FooterButton)`
    cursor: pointer;

    > span:first-child {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        text-align: center;
    }
`;